import type { FlatNamespace, TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFormattingLocale, languageConfigs, toSupportedLanguage } from '../i18n/language-config.ts';

export type TranslationFunction = TFunction<FlatNamespace>;

export function useLocale(namespace?: FlatNamespace | FlatNamespace[]) {
    const { i18n, t } = useTranslation(namespace);
    const language = toSupportedLanguage(i18n.language);

    const isI18nReady = Object.keys(i18n).length > 0;

    const formattingLocale = getFormattingLocale(language);

    const { thousandSeparator, decimalSeparator } = useMemo(() => {
        const parts = new Intl.NumberFormat(formattingLocale).formatToParts(1234567.8);
        return {
            thousandSeparator: parts.find((part) => part.type === 'group')?.value ?? '.',
            decimalSeparator: parts.find((part) => part.type === 'decimal')?.value ?? ',',
        };
    }, [formattingLocale]);

    const monthNames = useMemo(() => {
        const formatter = new Intl.DateTimeFormat(formattingLocale, { month: 'long' });
        return Array.from({ length: 12 }, (_, i) => formatter.format(new Date(2020, i, 10)));
    }, [formattingLocale]);

    return {
        i18n,
        t,
        isI18nReady,
        /** Translation language ('de'), for language logic */
        language,
        /** BCP 47 formatting locale ('de-DE'), for every Intl call */
        locale: formattingLocale,
        thousandSeparator,
        decimalSeparator,
        dateFnsLocalization: languageConfigs[language].dateFnsLocalization,
        monthNames,
    };
}
